import { z } from 'zod';
import {
  IMPORT_SCENARIO_MAX,
  SCENARIO_PRIORITY,
  SCENARIO_STATUS,
  ScenarioRequirementsSchema,
  ScenarioSequenceInputSchema,
} from './scenarios';

export const RYNORA_SCENARIO_EXPORT_FORMAT = 'rynora-scenarios' as const;
export const RYNORA_SCENARIO_EXPORT_VERSION = '1.0' as const;

export const SCENARIO_IMPORT_FORMATS = ['rynora-json', 'csv', 'excel', 'gherkin', 'ado', 'qmetry'] as const;
export type ScenarioImportFormat = (typeof SCENARIO_IMPORT_FORMATS)[number];

export const SCENARIO_EXPORT_FORMATS = ['rynora-json', 'csv', 'excel', 'gherkin', 'ado', 'qmetry'] as const;
export type ScenarioExportFormat = (typeof SCENARIO_EXPORT_FORMATS)[number];

export const SCENARIO_IMPORT_VALIDATION = ['valid', 'warning', 'error'] as const;
export type ScenarioImportValidation = (typeof SCENARIO_IMPORT_VALIDATION)[number];

export const SCENARIO_IMPORT_ACTIONS = ['create', 'update', 'skip'] as const;
export type ScenarioImportAction = (typeof SCENARIO_IMPORT_ACTIONS)[number];

export const SCENARIO_IMPORT_DUPLICATE_MODES = ['create', 'skip', 'update'] as const;
export type ScenarioImportDuplicateMode = (typeof SCENARIO_IMPORT_DUPLICATE_MODES)[number];

export const SCENARIO_MISSING_REQUIREMENT_ACTIONS = ['create', 'ignore', 'cancel'] as const;
export type ScenarioMissingRequirementAction = (typeof SCENARIO_MISSING_REQUIREMENT_ACTIONS)[number];

export const SCENARIO_MISSING_COMPONENT_ACTIONS = ['skip', 'create', 'cancel'] as const;
export type ScenarioMissingComponentAction = (typeof SCENARIO_MISSING_COMPONENT_ACTIONS)[number];

/** Canonical import draft — maps to CreateScenarioRequest / PatchScenarioRequest. */
export const ScenarioImportDraftSchema = z.object({
  sourceKey: z.string().min(1).max(120),
  id: z.string().uuid().optional(),
  key: z.string().max(80).optional(),
  externalId: z.string().max(120).optional(),
  title: z.string().trim().min(1).max(200),
  description: z.string().max(2_000).optional(),
  status: z.enum(SCENARIO_STATUS).optional(),
  priority: z.enum(SCENARIO_PRIORITY).optional(),
  tags: z.array(z.string().max(40)).max(20).optional(),
  requirementKeys: z.array(z.string().max(80)).max(20).default([]),
  businessFlowId: z.string().uuid().optional(),
  businessFlowName: z.string().max(120).optional(),
  given: z.string().max(2_000).optional(),
  when: z.string().max(2_000).optional(),
  then: z.string().max(2_000).optional(),
  preconditions: z.array(z.string().max(300)).max(20).optional(),
  steps: z.array(ScenarioSequenceInputSchema).max(50).default([]),
  testData: z.string().max(8_000).optional(),
  requirements: ScenarioRequirementsSchema.optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});
export type ScenarioImportDraft = z.infer<typeof ScenarioImportDraftSchema>;

export const ScenarioImportIssueSchema = z.object({
  level: z.enum(SCENARIO_IMPORT_VALIDATION),
  message: z.string().min(1).max(500),
  field: z.string().max(80).optional(),
});
export type ScenarioImportIssue = z.infer<typeof ScenarioImportIssueSchema>;

export const ScenarioImportPreviewItemSchema = z.object({
  sourceKey: z.string().min(1).max(120),
  title: z.string(),
  key: z.string(),
  action: z.enum(SCENARIO_IMPORT_ACTIONS),
  status: z.enum(SCENARIO_IMPORT_VALIDATION),
  duplicateOfId: z.string().uuid().nullable().optional(),
  requirementKeys: z.array(z.string()),
  componentKeys: z.array(z.string()),
  testDataKeys: z.array(z.string()),
  missingRequirements: z.array(z.string()),
  missingComponents: z.array(z.string()),
  missingTestData: z.array(z.string()),
  issues: z.array(ScenarioImportIssueSchema),
  draft: ScenarioImportDraftSchema,
});
export type ScenarioImportPreviewItem = z.infer<typeof ScenarioImportPreviewItemSchema>;

export const ScenarioImportColumnMappingSchema = z.record(z.string(), z.string());
export type ScenarioImportColumnMapping = z.infer<typeof ScenarioImportColumnMappingSchema>;

export const ScenarioImportPreviewRequestSchema = z.object({
  format: z.enum(SCENARIO_IMPORT_FORMATS),
  content: z.string().max(2_000_000),
  fileName: z.string().max(200).optional(),
  sheetName: z.string().max(120).optional(),
  columnMapping: ScenarioImportColumnMappingSchema.optional(),
  defaultBusinessFlowId: z.string().uuid().optional(),
});
export type ScenarioImportPreviewRequest = z.infer<typeof ScenarioImportPreviewRequestSchema>;

export const ScenarioImportPreviewSummarySchema = z.object({
  total: z.number().int().nonnegative(),
  newCount: z.number().int().nonnegative(),
  duplicateCount: z.number().int().nonnegative(),
  warningCount: z.number().int().nonnegative(),
  errorCount: z.number().int().nonnegative(),
});
export type ScenarioImportPreviewSummary = z.infer<typeof ScenarioImportPreviewSummarySchema>;

export const ScenarioImportPreviewResponseSchema = z.object({
  summary: ScenarioImportPreviewSummarySchema,
  items: z.array(ScenarioImportPreviewItemSchema).max(IMPORT_SCENARIO_MAX),
  sheetNames: z.array(z.string()).optional(),
  selectedSheet: z.string().optional(),
  availableColumns: z.array(z.string()).optional(),
  warnings: z.array(z.string()).default([]),
});
export type ScenarioImportPreviewResponse = z.infer<typeof ScenarioImportPreviewResponseSchema>;

export const ScenarioImportConfirmItemSchema = z.object({
  sourceKey: z.string().min(1).max(120),
  action: z.enum(SCENARIO_IMPORT_ACTIONS),
  draft: ScenarioImportDraftSchema,
  targetScenarioId: z.string().uuid().optional(),
});
export type ScenarioImportConfirmItem = z.infer<typeof ScenarioImportConfirmItemSchema>;

export const ScenarioImportConfirmRequestSchema = z.object({
  items: z.array(ScenarioImportConfirmItemSchema).min(1).max(IMPORT_SCENARIO_MAX),
  duplicateMode: z.enum(SCENARIO_IMPORT_DUPLICATE_MODES).default('skip'),
  missingRequirementAction: z.enum(SCENARIO_MISSING_REQUIREMENT_ACTIONS).default('ignore'),
  missingComponentAction: z.enum(SCENARIO_MISSING_COMPONENT_ACTIONS).default('skip'),
  embeddedComponents: z
    .array(
      z.object({
        key: z.string().min(1).max(80),
        name: z.string().min(1).max(200),
        description: z.string().max(2_000).optional(),
        steps: z.array(ScenarioSequenceInputSchema).max(50).default([]),
        parameters: z.array(z.string()).optional(),
      }),
    )
    .max(50)
    .optional(),
});
export type ScenarioImportConfirmRequest = z.infer<typeof ScenarioImportConfirmRequestSchema>;

export const ScenarioImportConfirmResponseSchema = z.object({
  created: z.number().int().nonnegative(),
  updated: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  errors: z.array(z.string()).default([]),
});
export type ScenarioImportConfirmResponse = z.infer<typeof ScenarioImportConfirmResponseSchema>;

export const ScenarioExportRequestSchema = z.object({
  format: z.enum(SCENARIO_EXPORT_FORMATS),
  scenarioIds: z.array(z.string().uuid()).max(IMPORT_SCENARIO_MAX).optional(),
  scope: z.enum(['selected', 'all']).default('selected'),
});
export type ScenarioExportRequest = z.infer<typeof ScenarioExportRequestSchema>;

export const ScenarioExportResponseSchema = z.object({
  fileName: z.string(),
  mimeType: z.string(),
  content: z.string(),
  encoding: z.enum(['utf-8', 'base64']),
});
export type ScenarioExportResponse = z.infer<typeof ScenarioExportResponseSchema>;

/** Lossless Rynora JSON bundle shape. */
export const RynoraScenarioExportRecordSchema = z.object({
  id: z.string().uuid().optional(),
  key: z.string().optional(),
  externalId: z.string().optional(),
  title: z.string(),
  description: z.string().optional(),
  status: z.enum(SCENARIO_STATUS).optional(),
  priority: z.enum(SCENARIO_PRIORITY).optional(),
  tags: z.array(z.string()).optional(),
  requirementKeys: z.array(z.string()).optional(),
  businessFlow: z.string().optional(),
  businessFlowId: z.string().uuid().optional(),
  given: z.string().optional(),
  when: z.string().optional(),
  then: z.string().optional(),
  preconditions: z.array(z.string()).optional(),
  steps: z.array(z.unknown()).optional(),
  testData: z.string().optional(),
  requirements: ScenarioRequirementsSchema.optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});
export type RynoraScenarioExportRecord = z.infer<typeof RynoraScenarioExportRecordSchema>;

export const RynoraScenarioExportBundleSchema = z.object({
  format: z.literal(RYNORA_SCENARIO_EXPORT_FORMAT),
  version: z.literal(RYNORA_SCENARIO_EXPORT_VERSION),
  exportedAt: z.string(),
  scenarios: z.array(RynoraScenarioExportRecordSchema),
  components: z
    .array(
      z.object({
        key: z.string(),
        name: z.string(),
        description: z.string().optional(),
        steps: z.array(z.unknown()).optional(),
        parameters: z.array(z.string()).optional(),
      }),
    )
    .optional(),
});
export type RynoraScenarioExportBundle = z.infer<typeof RynoraScenarioExportBundleSchema>;
